import type { EventId, PlaceId, TagId } from "./ids";
import type { SourceLink, OfficialBadge, VerificationStatus } from "./source";

export const EVENT_STATUSES = ["SCHEDULED", "ONGOING", "ENDED", "CANCELLED"] as const;
export type EventStatus = (typeof EVENT_STATUSES)[number];

export interface EventPlaceRef {
  id: PlaceId; name: string; addressLine: string;
  latitude?: number; longitude?: number;
}

export interface EventSummary {
  id: EventId;
  name: string;
  summary?: string | null;
  eventType?: string | null;
  startsAt: string;
  endsAt: string;
  bookingStartsAt?: string | null;
  priceSummary?: string | null;
  status: EventStatus | string;
  verificationStatus: VerificationStatus;
  reservationRequired: boolean;
  officialBadge?: OfficialBadge | null;
  tags?: Array<{ id: TagId; name: string }>;
  sourceLinks?: SourceLink[];
  places?: EventPlaceRef[];
}

/** I-EV-02: Event ↔ Place 는 N:M. */
export interface OshiEvent extends EventSummary {
  places: EventPlaceRef[];
}

/**
 * 이벤트들이 열리는 **서로 다른 장소** 수.
 *
 * 화면이 "곳" 이라고 썼으면 장소를 세야 합니다. 한 곳에서 두 이벤트가 열려도
 * 한 곳입니다 — 이벤트 수를 세면 사용자가 갈 데를 실제보다 많게 봅니다 (AC-14).
 *
 * 장소 정보가 없는 이벤트는 그 자체를 한 곳으로 셉니다. 어디인지 모를 뿐
 * 갈 데가 있는 것은 맞아서, 0으로 지우면 있는 것을 없다고 말하게 됩니다.
 *
 * Web 과 Native 가 각자 세고 있었습니다. 같은 규칙을 두 곳에 두면 갈라집니다.
 */
export function distinctPlaceCount(events: readonly { places?: readonly { id: string }[] }[]): number {
  const ids = new Set<string>();
  let withoutPlace = 0;
  for (const event of events) {
    const places = event.places ?? [];
    if (places.length === 0) withoutPlace += 1;
    for (const place of places) ids.add(place.id);
  }
  return ids.size + withoutPlace;
}
